import React from 'react';
import { connect } from 'react-redux';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Select, { SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';

import { Accessibility } from '@apps/shared/src/providerSearch/types/providerSearch';
import { setAccessibilities } from '@apps/shared/src/providerSearch/actions';
import { accessibilityLabel } from '@apps/shared/src/providerSearch/api/utilities';
import { toSentenceCase } from '@apps/shared/src/utilities/stringUtilities';
import { allAccessibilities } from '@apps/shared/src/providerSearch/api/providers';
import { RootState } from '../../store';
import { IncompleteTheme } from '../../shared/types/theme';

const Header = styled(Typography)(({ theme }) => ({
  ...(theme as IncompleteTheme).sidebar.headerText,
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  ...(theme as IncompleteTheme).selectMenuItem,
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  minHeight: '48px',
}));

const StyledSelect = styled(Select)({
  width: '100%',
  '& .MuiSelect-select': {
    background: '#ffffff',
    border: '1px solid #E5EBF5',
    padding: '12px 16px',
  },
});

const mapStateToProps = ({ providerSearch }: RootState): StateProps => ({
  selectedAccessibilities: providerSearch.selectedAccessibilities,
  isLoading: providerSearch.isLoadingProviderList,
  accessibiltyCounts: providerSearch.accessibiltyCounts, // use directly from redux
});

const mapDispatchToProps = {
  setAccessibilities,
};

type StateProps = {
  selectedAccessibilities: Accessibility[];
  isLoading: boolean;
  accessibiltyCounts: Record<Accessibility, number>;
};

type Props = StateProps & typeof mapDispatchToProps;

export function AccessibilityFilter({
  selectedAccessibilities,
  accessibiltyCounts,
  setAccessibilities,
  isLoading,
}: Props): JSX.Element {
  const handleProviderAccessibilityChange: SelectProps['onChange'] = e => {
    const levels = e.target.value as Accessibility[];
    setAccessibilities(levels);
  };

  const nonEmptyAccessibilities = allAccessibilities.filter(a => accessibiltyCounts[a] > 0);

  const accessibilityValue =
    selectedAccessibilities.length === 0
      ? nonEmptyAccessibilities
      : selectedAccessibilities.filter(a => accessibiltyCounts[a] > 0);

  const renderAccessibilitySelectValue: SelectProps['renderValue'] = () =>
    accessibilityValue.map(toSentenceCase).join(', ');

  return (
    <div>
      <Header>Accessibility</Header>
      <StyledSelect
        labelId="provider-accessibility"
        id="accessibility-search"
        value={accessibilityValue}
        onChange={handleProviderAccessibilityChange}
        disableUnderline
        disabled={isLoading}
        multiple
        MenuProps={{ variant: 'menu' }}
        renderValue={renderAccessibilitySelectValue}
      >
        {nonEmptyAccessibilities.map(a => (
          <StyledMenuItem key={a} value={a}>
            <Checkbox color="primary" checked={selectedAccessibilities.includes(a)} />
            {accessibilityLabel(a, accessibiltyCounts[a])}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </div>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(AccessibilityFilter);
